/**
 * Pac-Man sound effects (C) — synthesised with the Web Audio API, same shape
 * as the Space Invaders module (unlock/play/setMuted/isMuted/dispose).
 * Engine events (state.events) are forwarded here by the component.
 */
export const WAKA_MIN_GAP_S = 0.09;

const SILENT = 0.0001;

function defaultAudioContextFactory() {
    if (typeof window === 'undefined') {
        return null;
    }
    const Ctor = window.AudioContext || window.webkitAudioContext;
    return Ctor ? new Ctor() : null;
}

export function createPacmanSound({ audioContextFactory = defaultAudioContextFactory, muted = false } = {}) {
    let ctx = null;
    let triedFactory = false;
    let isMuted = !!muted;
    let disposed = false;
    let siren = null;
    let wakaFlip = false;
    let lastWakaAt = -Infinity;

    const now = () => (ctx && typeof ctx.currentTime === 'number' ? ctx.currentTime : 0);

    function envelope(gainValue, start, duration) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(gainValue, start);
        g.gain.exponentialRampToValueAtTime(SILENT, start + duration);
        g.connect(ctx.destination);
        return g;
    }

    function tone({
        freq, endFreq = null, type = 'square', duration = 0.1, gain = 0.15, at = 0,
    }) {
        const start = now() + at;
        const osc = ctx.createOscillator();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, start);
        if (endFreq) {
            osc.frequency.exponentialRampToValueAtTime(endFreq, start + duration);
        }
        osc.connect(envelope(gain, start, duration));
        osc.start(start);
        osc.stop(start + duration + 0.02);
    }

    function startSiren() {
        if (siren) {
            return;
        }
        const start = now();
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(300, start);
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(4, start);
        const depth = ctx.createGain();
        depth.gain.setValueAtTime(120, start);
        lfo.connect(depth);
        depth.connect(osc.frequency);
        const out = ctx.createGain();
        out.gain.setValueAtTime(0.06, start);
        osc.connect(out);
        out.connect(ctx.destination);
        osc.start(start);
        lfo.start(start);
        siren = { osc, lfo };
    }

    function stopSiren() {
        if (!siren) {
            return;
        }
        const stopAt = now();
        siren.osc.stop(stopAt);
        siren.lfo.stop(stopAt);
        siren = null;
    }

    const effects = {
        pellet: () => {
            if (now() - lastWakaAt < WAKA_MIN_GAP_S) {
                return;
            }
            lastWakaAt = now();
            wakaFlip = !wakaFlip;
            tone({
                freq: wakaFlip ? 440 : 220, endFreq: wakaFlip ? 220 : 440, type: 'square', duration: 0.06, gain: 0.12,
            });
        },
        power: () => startSiren(),
        frightStart: () => startSiren(),
        frightEnd: () => stopSiren(),
        ghostEaten: () => tone({
            freq: 400, endFreq: 1600, type: 'square', duration: 0.3, gain: 0.15,
        }),
        death: () => {
            stopSiren();
            [600, 480, 380, 260, 150].forEach((freq, i) => tone({
                freq, endFreq: freq * 0.7, type: 'sawtooth', duration: 0.22, gain: 0.18, at: i * 0.24,
            }));
        },
        start: () => {
            stopSiren();
            [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => tone({
                freq, type: 'square', duration: 0.14, gain: 0.14, at: i * 0.15,
            }));
        },
        levelCleared: () => {
            stopSiren();
            [523.25, 587.33, 659.25, 783.99, 880, 1046.5].forEach((freq, i) => tone({
                freq, type: 'square', duration: 0.1, gain: 0.14, at: i * 0.09,
            }));
        },
        extraLife: () => {
            [1568, 2093].forEach((freq, i) => tone({
                freq, type: 'triangle', duration: 0.18, gain: 0.16, at: i * 0.16,
            }));
        },
        gameOver: () => {
            stopSiren();
            [330, 262, 196].forEach((freq, i) => tone({
                freq, type: 'sawtooth', duration: 0.22, gain: 0.18, at: i * 0.2,
            }));
        },
    };

    return {
        unlock() {
            if (disposed) {
                return;
            }
            if (!ctx && !triedFactory) {
                triedFactory = true;
                ctx = audioContextFactory() || null;
            }
            if (ctx && ctx.state !== 'running' && typeof ctx.resume === 'function') {
                const resumed = ctx.resume();
                if (resumed && typeof resumed.catch === 'function') {
                    resumed.catch(() => {});
                }
            }
        },
        play(event) {
            if (!ctx || disposed || isMuted || !event) {
                return;
            }
            const effect = effects[event.type];
            if (effect) {
                effect(event);
            }
        },
        setMuted(value) {
            isMuted = !!value;
            if (isMuted && ctx) {
                stopSiren();
            }
        },
        isMuted() {
            return isMuted;
        },
        dispose() {
            if (ctx) {
                stopSiren();
                if (typeof ctx.close === 'function') {
                    const closed = ctx.close();
                    if (closed && typeof closed.catch === 'function') {
                        closed.catch(() => {});
                    }
                }
            }
            disposed = true;
        },
    };
}
